import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { forkJoin } from 'rxjs';
import { AnalyticsService } from '../../../services/analytics.service';
import { DespesaGrupoApi, DespesaSubgrupoApi } from '../../../models/analytics.model';
import { DateUtilService } from '../../../services/dateUtil.service';
import { ToastrService } from 'ngx-toastr';

interface GrupoAgregado {
  grupo: string;
  valorPago: number;
  pctDoTotalGeral: number;
  color: string;
  slicePath: string;
}

interface SubgrupoItem {
  subgrupo: string;
  valorPago: number;
  pctDoTotalGeral: number;
  color: string;
  barWidth: number;
}

@Component({
  selector: 'app-despesas-mensal',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DecimalPipe],
  templateUrl: './despesas-mensal.component.html',
  styleUrl: './despesas-mensal.component.css'
})
export class DespesasMensalComponent implements OnInit {
  isLoading = true;
  hasError  = false;

  grupos:    GrupoAgregado[] = [];
  subgrupos: SubgrupoItem[]  = [];
  totalGeral = 0;

  readonly dataInicial: string;
  readonly dataFinal:   string;

  tooltipGrupo: GrupoAgregado | null = null;

  private readonly COLORS = [
    '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#14b8a6',
    '#a855f7', '#0ea5e9'
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private dateUtilService: DateUtilService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    const hoje       = this.dateUtilService.dataHoje;
    const [y, m]     = hoje.split('-');
    this.dataInicial = `${y}-${m}-01`;
    this.dataFinal   = hoje;
  }

  ngOnInit(): void { this.carregarDados(); }

  carregarDados(): void {
    this.isLoading = true;
    this.hasError  = false;

    forkJoin({
      grupos:    this.analyticsService.getDespesasPorGrupo(this.dataInicial, this.dataFinal),
      subgrupos: this.analyticsService.getDespesasPorSubgrupo(this.dataInicial, this.dataFinal)
    }).subscribe({
      next: ({ grupos, subgrupos }) => {
        this.processarGrupos(Array.isArray(grupos) ? grupos : []);
        this.processarSubgrupos(Array.isArray(subgrupos) ? subgrupos : []);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.hasError  = true;
        this.isLoading = false;
        this.toastr.error('Não foi possível carregar as despesas mensais.');
        this.cdr.detectChanges();
      }
    });
  }

  private processarGrupos(dados: DespesaGrupoApi[]): void {
    if (!dados.length) { this.grupos = []; this.totalGeral = 0; return; }

    const ordenados = dados.slice().sort((a, b) => b.pctDoTotalGeral - a.pctDoTotalGeral);
    this.totalGeral = ordenados.reduce((s, g) => s + g.valorPago, 0);

    const CX = 100, CY = 100, R = 80, IR = 48, GAP = 1.5;
    let angle = 0;

    this.grupos = ordenados.map((g, i) => {
      const sweep = (g.pctDoTotalGeral / 100) * 360;
      const start = angle + GAP / 2;
      const end   = angle + sweep - GAP / 2;
      angle      += sweep;
      return {
        grupo:           g.grupo,
        valorPago:       g.valorPago,
        pctDoTotalGeral: g.pctDoTotalGeral,
        color:           this.COLORS[i % this.COLORS.length],
        slicePath:       sweep > GAP ? this.buildSlice(CX, CY, R, IR, start, end) : ''
      };
    });
  }

  private processarSubgrupos(dados: DespesaSubgrupoApi[]): void {
    if (!dados.length) { this.subgrupos = []; return; }

    const ordenados = dados.slice().sort((a, b) => b.pctDoTotalGeral - a.pctDoTotalGeral);
    const maxValor  = Math.max(...ordenados.map(d => d.valorPago), 1);

    this.subgrupos = ordenados.map((d, i) => ({
      subgrupo:        d.subgrupo,
      valorPago:       d.valorPago,
      pctDoTotalGeral: d.pctDoTotalGeral,
      color:           this.COLORS[i % this.COLORS.length],
      barWidth:        Math.max((d.valorPago / maxValor) * 100, 1)
    }));
  }

  private buildSlice(cx: number, cy: number, r: number, ir: number, startDeg: number, endDeg: number): string {
    const rad  = (d: number) => (d - 90) * Math.PI / 180;
    const x1   = cx + r  * Math.cos(rad(startDeg)), y1  = cy + r  * Math.sin(rad(startDeg));
    const x2   = cx + r  * Math.cos(rad(endDeg)),   y2  = cy + r  * Math.sin(rad(endDeg));
    const ix1  = cx + ir * Math.cos(rad(endDeg)),   iy1 = cy + ir * Math.sin(rad(endDeg));
    const ix2  = cx + ir * Math.cos(rad(startDeg)), iy2 = cy + ir * Math.sin(rad(startDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${ix1},${iy1} A${ir},${ir} 0 ${large} 0 ${ix2},${iy2}Z`;
  }

  showTooltip(g: GrupoAgregado): void { this.tooltipGrupo = g; }
  hideTooltip(): void                  { this.tooltipGrupo = null; }
}
